import { Card, Col, Row } from 'antd';
import * as echarts from 'echarts';
import { useMemo } from 'react';
import type { TaskStatisticsOutputDto } from '../model';
import { EChart } from './e-chart';

export function TaskStatisticsCharts(props: {
  accountStats: TaskStatisticsOutputDto['account_stats'];
  modelStats: TaskStatisticsOutputDto['model_stats'];
  departmentStats: TaskStatisticsOutputDto['department_stats'];
  mode?: 'full' | 'model-only';
  formatModelName?: (model: string) => string;
}) {
  const { formatModelName } = props;
  const model_only = props.mode === 'model-only';
  const sorted_account_stats = useMemo(() => {
    return [...props.accountStats].sort((a, b) => {
      return b.succeeded + b.failed - (a.succeeded + a.failed);
    });
  }, [props.accountStats]);

  const sorted_model_stats = useMemo(() => {
    return [...props.modelStats].sort((a, b) => {
      return b.succeeded + b.failed - (a.succeeded + a.failed);
    });
  }, [props.modelStats]);

  const account_chart_option = useMemo<echarts.EChartsOption>(() => {
    const accounts = sorted_account_stats;

    return {
      toolbox: {
        feature: {
          dataZoom: {
            yAxisIndex: false,
          },
        },
      },
      backgroundColor: 'transparent',
      color: ['#52c41a', '#ff4d4f', '#1677ff'],
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
      },
      legend: {
        top: 0,
        textStyle: {
          color: '#d9d9d9',
        },
      },
      grid: {
        left: 56,
        right: 56,
        top: 48,
        bottom: 88,
      },
      dataZoom: [
        {
          type: 'inside',
          end: 50,
          zoomOnMouseWheel: 'ctrl',
        },
        {
          type: 'slider',
        },
      ],
      xAxis: {
        type: 'category',
        data: accounts.map((item) => item.account_name),
        axisLabel: {
          interval: 0,
          rotate: 20,
        },
      },
      yAxis: [
        {
          type: 'value',
          name: '成功/失败数',
          splitLine: {
            lineStyle: {
              color: 'rgba(255,255,255,0.08)',
            },
          },
        },
        {
          type: 'value',
          name: '脚本数量',
          splitLine: {
            show: false,
          },
        },
      ],
      series: [
        {
          name: '成功数',
          type: 'bar',
          stack: 'task-result',
          barMaxWidth: 36,
          data: accounts.map((item) => item.succeeded),
        },
        {
          name: '失败数',
          type: 'bar',
          stack: 'task-result',
          barMaxWidth: 36,
          data: accounts.map((item) => item.failed),
        },
        {
          name: '脚本数量',
          type: 'bar',
          yAxisIndex: 1,
          stack: 'script-count',
          barMaxWidth: 20,
          data: accounts.map((item) => item.script_count),
        },
      ],
    };
  }, [sorted_account_stats]);

  const model_chart_option = useMemo<echarts.EChartsOption>(() => {
    const models = sorted_model_stats;

    return {
      backgroundColor: 'transparent',
      color: ['#52c41a', '#ff4d4f', '#1677ff'],
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
      },
      legend: {
        top: 0,
        textStyle: {
          color: '#d9d9d9',
        },
      },
      grid: {
        left: 48,
        right: 48,
        top: 48,
        bottom: 56,
      },
      xAxis: {
        type: 'category',
        data: models.map((item) => formatModelName?.(item.model) ?? item.model),
        axisLabel: {
          interval: 0,
          rotate: 20,
        },
      },
      yAxis: [
        {
          type: 'value',
          name: '任务数量',
          splitLine: {
            lineStyle: {
              color: 'rgba(255,255,255,0.08)',
            },
          },
        },
        {
          type: 'value',
          name: '信用点',
          splitLine: {
            show: false,
          },
        },
      ],
      series: [
        {
          name: '成功数',
          type: 'bar',
          stack: 'model-result',
          barMaxWidth: 40,
          data: models.map((item) => item.succeeded),
        },
        {
          name: '失败数',
          type: 'bar',
          stack: 'model-result',
          barMaxWidth: 40,
          data: models.map((item) => item.failed),
        },
        {
          name: '信用点',
          type: 'bar',
          yAxisIndex: 1,
          stack: 'model-cost',
          barMaxWidth: 20,
          data: models.map((item) => item.cost),
        },
      ],
    };
  }, [formatModelName, sorted_model_stats]);

  const departmentChartOption = useMemo<echarts.EChartsOption>(() => {
    const departments = props.departmentStats;

    return {
      backgroundColor: 'transparent',
      color: ['#52c41a'],
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
      },
      legend: {
        top: 0,
        textStyle: {
          color: '#d9d9d9',
        },
      },
      grid: {
        left: 48,
        right: 24,
        top: 48,
        bottom: 56,
      },
      xAxis: {
        type: 'category',
        data: departments.map((item) => item.department),
        axisLabel: {
          interval: 0,
          rotate: 20,
        },
      },
      yAxis: {
        type: 'value',
        name: '视频数量',
        splitLine: {
          lineStyle: {
            color: 'rgba(255,255,255,0.08)',
          },
        },
      },
      series: [
        {
          name: '视频数量',
          type: 'bar',
          stack: 'model-result',
          barMaxWidth: 40,
          label: {
            show: true,
            position: 'top',
            formatter: '{c}',
            color: '#000000',
            textBorderWidth: 0,
            textBorderColor: 'transparent',
          },
          data: props.departmentStats.map((item) => item.video_count),
        },
      ],
    };
  }, [props.departmentStats]);

  const credit_chart_option = useMemo<echarts.EChartsOption>(() => {
    const accounts = [...props.accountStats].sort((a, b) => b.cost - a.cost);

    return {
      toolbox: {
        feature: {
          dataZoom: {
            yAxisIndex: false,
          },
        },
      },
      backgroundColor: 'transparent',
      color: ['#1677ff'],
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
      },
      grid: {
        left: 56,
        right: 56,
        top: 32,
        bottom: 88,
      },
      dataZoom: [
        {
          type: 'inside',
          end: 50,
          zoomOnMouseWheel: 'ctrl',
        },
        {
          type: 'slider',
        },
      ],
      xAxis: {
        type: 'category',
        data: accounts.map((item) => item.account_name),
        axisLabel: {
          interval: 0,
          rotate: 20,
        },
      },
      yAxis: {
        type: 'value',
        name: '信用点',
        splitLine: {
          lineStyle: {
            color: 'rgba(255,255,255,0.08)',
          },
        },
      },
      series: [
        {
          name: '信用点使用量',
          type: 'bar',
          barMaxWidth: 40,
          label: {
            show: true,
            position: 'top',
            formatter: '{c}',
            color: '#000000',
            textBorderWidth: 0,
            textBorderColor: 'transparent',
          },
          data: accounts.map((item) => item.cost),
        },
      ],
    };
  }, [props.accountStats]);

  return (
    <Row gutter={[16, 16]}>
      {!model_only && (
        <Col span={24}>
          <Card title="账户统计图">
            <EChart option={account_chart_option} />
          </Card>
        </Col>
      )}
      {!model_only && (
        <Col span={24}>
          <Card title="信用点统计图">
            <EChart option={credit_chart_option} />
          </Card>
        </Col>
      )}
      <Col xs={24} xl={model_only ? 24 : 12}>
        <Card title="模型统计图">
          <EChart option={model_chart_option} />
        </Card>
      </Col>
      {!model_only && (
        <Col xs={24} xl={12}>
          <Card title="部门统计图">
            <EChart option={departmentChartOption} />
          </Card>
        </Col>
      )}
    </Row>
  );
}
